"use client";

import { useState } from "react";
import { HandHeart, Lock, RefreshCw } from "lucide-react";
import { RequireRole } from "../console-shell";
import { api, commandId, formatDate, formatTime, reasonText } from "../../lib/api";
import { Banner, Button, ButtonRow, ConfirmPanel, DeskCard, Desk, Fact, Facts, Note, PageHeader, Segmented, StatusTag } from ".";
import { ReadState, useRead, type PageResult } from "./record-tools";

type Bucket = {
  audienceCount: number; reportedCount: number; nilCount: number; noResponseCount: number; recordCount: number;
  volunteerCount: number; fundsRecordCount: number; fundsTotal: string; goodsRecordCount: number;
};
type TaskSummary = {
  id: string; publicRef: string; status: "PUBLISHED" | "CLOSED" | "WITHDRAWN"; frozen: boolean; version: number; title: string;
  periodYear: number; periodQuarter: number; publishedAt: string; closesAt: string; withdrawnReason?: string; audienceCount: number; respondedCount: number;
};
type TaskDetail = TaskSummary & {
  stats: { asOf: string; frozen: boolean; basis: string; totals: Bucket; byOrganization: Array<Bucket & { organizationId: string; organizationName: string }>; lateRecordCount: number; outsideRecordCount: number };
};
type Reminder = { knownAt: string; canPublish: boolean; due?: { periodYear: number; periodQuarter: number; label: string }; open?: TaskSummary };
type RecordRow = {
  id: string; publicRef: string; status: "ACTIVE" | "WITHDRAWN"; title: string; occurredOn: string; periodYear: number; periodQuarter: number;
  participationKinds: string[]; memberDisplayName: string; organization: { id: string; name: string }; updatedAt: string;
};
type RecordDetail = RecordRow & { organizerLabel?: string; fundsAmount?: string; goodsDescription?: string; note?: string; recordedAt: string; history: Array<{ sequence: number; action: string; occurredAt: string }> };

const QUARTER = ["", "第一季度", "第二季度", "第三季度", "第四季度"];
const period = (y: number, q: number) => `${y} 年${QUARTER[q]}`;
const KIND: Record<string, string> = { VOLUNTEER: "到场服务", FUNDS: "捐款", GOODS: "捐物" };
const kinds = (list: string[]) => ["VOLUNTEER", "FUNDS", "GOODS"].filter((k) => list.includes(k)).map((k) => KIND[k]).join("、");
const yuan = (amount: string) => `${amount.replace(/\B(?=(\d{3})+(?!\d)(\.|$))/g, ",")} 元`;
const ACTION: Record<string, string> = { CREATED: "本人新建", AMENDED: "本人修改", WITHDRAWN: "本人撤回" };

// 开放中是办；截止即定格是终；撤回是实底不加勾（DESIGN §4 状态四形）。
function taskTag(task: TaskSummary) {
  if (task.status === "WITHDRAWN") return <StatusTag tone="neutral">已撤回</StatusTag>;
  return task.frozen ? <StatusTag tone="success">已定格</StatusTag> : <StatusTag tone="brand">填报中</StatusTag>;
}

export function CharityWorkspace() {
  return (
    <RequireRole roleCode="SOCIAL_SERVICE_COORDINATOR">
      <CharityDesk />
    </RequireRole>
  );
}

function CharityDesk() {
  const reminder = useRead<Reminder>("/api/v1/charity/reminder");
  const tasks = useRead<{ items: TaskSummary[] }>("/api/v1/charity/tasks");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [view, setView] = useState("stats");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const current = selectedId ?? tasks.data?.items.find((t) => t.status !== "WITHDRAWN")?.id ?? null;
  const detail = useRead<TaskDetail>(current ? `/api/v1/charity/tasks/${current}` : null);

  const reloadAll = () => { void reminder.reload(); void tasks.reload(); void detail.reload(); };
  const publish = async () => {
    const due = reminder.data?.due;
    if (!due) return;
    setBusy(true); setError("");
    try {
      const created = await api<TaskDetail>("/api/v1/charity/tasks", { method: "POST", body: { commandId: commandId(), periodYear: due.periodYear, periodQuarter: due.periodQuarter } });
      setConfirming(false);
      setSelectedId(created.id);
      reloadAll();
    } catch (caught) {
      setError(reasonText(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Desk wide>
      <PageHeader
        eyebrow="会务与履职 / 社会服务统计"
        title="社会服务统计"
        description="会员本人填报参加过的社会服务活动；每季度发布一次统计任务，全员收到站内通知。统计只汇总到组织，不对个人合计或排序，不公开。"
        actions={<Button variant="ghost" aria-label="刷新社会服务统计" onClick={reloadAll}><RefreshCw size={16} /></Button>}
      />
      <ReadState {...reminder} retry={reminder.reload} />
      {reminder.data?.due ? (
        <Banner tone="warning">
          <span className="flex flex-wrap items-center justify-between gap-3">
            <span><strong>{reminder.data.due.label}</strong> · 季度结束后由市级经办人发布；发布即冻结收件人并发送站内通知。</span>
            {!confirming ? <Button size="sm" onClick={() => setConfirming(true)}>发布{period(reminder.data.due.periodYear, reminder.data.due.periodQuarter)}统计</Button> : null}
          </span>
        </Banner>
      ) : null}
      {confirming && reminder.data?.due ? (
        <ConfirmPanel
          title={`发布${period(reminder.data.due.periodYear, reminder.data.due.periodQuarter)}统计`}
          consequence="将向全体有效会员发送一条固定内容的站内通知，请他们填报或回应「本季度无」；截止为发布后 15 天。发布后不能改期，只能提前截止或写明原因撤回。"
          confirmLabel="发布"
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void publish()}
        />
      ) : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}

      <div className="grid items-start gap-4 lg:grid-cols-[var(--d-queue)_minmax(0,1fr)]">
        <DeskCard title="统计任务">
          <ReadState {...tasks} retry={tasks.reload} />
          <ul className="list-none p-0">
            {tasks.data?.items.map((task) => (
              <li key={task.id} className="not-first:border-t">
                <button type="button" onClick={() => setSelectedId(task.id)} className={`flex w-full flex-col gap-1 px-3 py-3 text-left ${current === task.id ? "bg-[var(--brand-soft)]" : "hover:bg-muted"}`}>
                  <span className="flex items-start justify-between gap-3"><span className="text-sm font-semibold">{period(task.periodYear, task.periodQuarter)}</span>{taskTag(task)}</span>
                  <span className="text-xs tabular-nums text-muted-foreground">已回应 {task.respondedCount} / {task.audienceCount} 人 · {formatDate(task.closesAt)} 截止</span>
                </button>
              </li>
            ))}
          </ul>
          {tasks.data && !tasks.data.items.length ? <p className="px-3 py-6 text-xs text-muted-foreground">还没有统计任务。上一季度结束后，这里会提醒发布。</p> : null}
        </DeskCard>

        <div>
          {detail.data ? (
            <>
              <Segmented value={view} onChange={setView} options={[{ value: "stats", label: "组织汇总" }, { value: "records", label: "填报明细" }]} />
              <div className="mt-4">{view === "stats" ? <TaskStats task={detail.data} onChanged={reloadAll} /> : <RecordsDesk task={detail.data} />}</div>
            </>
          ) : (
            <ReadState {...detail} retry={detail.reload} />
          )}
        </div>
      </div>
    </Desk>
  );
}

function TaskStats({ task, onChanged }: { task: TaskDetail; onChanged: () => void }) {
  const [closing, setClosing] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { stats } = task;
  const run = async (path: string, body: object) => {
    setBusy(true); setError("");
    try { await api(path, { method: "POST", body }); setClosing(false); setWithdrawing(false); onChanged(); }
    catch (caught) { setError(reasonText(caught)); }
    finally { setBusy(false); }
  };
  return (
    <DeskCard
      title={<span className="flex items-center gap-3">{period(task.periodYear, task.periodQuarter)}汇总 {taskTag(task)}</span>}
      description={`统计时点 ${formatTime(stats.asOf)}${stats.frozen ? "（已定格）" : "（填报中，数字会变）"} · ${task.publicRef}`}
      actions={task.status === "PUBLISHED" && !task.frozen ? <ButtonRow>
        <Button variant="outline" size="sm" onClick={() => setClosing(true)}>提前截止</Button>
        <Button variant="ghost" size="sm" onClick={() => setWithdrawing(true)}>撤回</Button>
      </ButtonRow> : task.status !== "WITHDRAWN" ? <Button variant="ghost" size="sm" onClick={() => setWithdrawing(true)}>撤回</Button> : null}
    >
      {closing ? <ConfirmPanel title="提前截止" consequence="截止后该季度统计即定格；会员不能再修改这一季度的记录，之后补记的不计入。" confirmLabel="截止" busy={busy}
        onCancel={() => setClosing(false)} onConfirm={() => void run(`/api/v1/charity/tasks/${task.id}/close`, { commandId: commandId(), expectedVersion: task.version })} /> : null}
      {withdrawing ? <ConfirmPanel title="撤回统计任务" consequence="撤回后那条站内通知一并撤回，这一季度可以重新发布。已填报的记录仍保留为会员本人的历史。" confirmLabel="撤回" busy={busy} confirmDisabled={!note.trim()}
        onCancel={() => setWithdrawing(false)} onConfirm={() => void run(`/api/v1/charity/tasks/${task.id}/withdraw`, { commandId: commandId(), expectedVersion: task.version, note: note.trim() })}>
        <input className="w-full border px-3 py-2 text-sm" placeholder="撤回原因（必填）" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} aria-label="撤回原因" />
      </ConfirmPanel> : null}
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {task.withdrawnReason ? <Note>撤回原因：{task.withdrawnReason}</Note> : null}
      <div className="record-table-scroll mt-4">
        <table className="record-table">
          <caption className="sr-only">按组织汇总的社会服务参与情况</caption>
          <thead>
            <tr><th>组织</th><th className="text-right">收件人</th><th className="text-right">已填报</th><th className="text-right">本季度无</th><th className="text-right">未回应</th><th className="text-right">人次</th><th className="text-right">捐款</th><th className="text-right">捐款合计</th><th className="text-right">捐物</th></tr>
          </thead>
          <tbody className="tabular-nums">
            {stats.byOrganization.map((row) => (
              <tr key={row.organizationId}>
                <td>{row.organizationName}</td><td className="text-right">{row.audienceCount}</td><td className="text-right">{row.reportedCount}</td><td className="text-right">{row.nilCount}</td>
                <td className="text-right">{row.noResponseCount}</td><td className="text-right">{row.recordCount}</td><td className="text-right">{row.fundsRecordCount} 条</td><td className="text-right whitespace-nowrap">{yuan(row.fundsTotal)}</td><td className="text-right">{row.goodsRecordCount} 条</td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td>合计</td><td className="text-right">{stats.totals.audienceCount}</td><td className="text-right">{stats.totals.reportedCount}</td><td className="text-right">{stats.totals.nilCount}</td>
              <td className="text-right">{stats.totals.noResponseCount}</td><td className="text-right">{stats.totals.recordCount}</td><td className="text-right">{stats.totals.fundsRecordCount} 条</td><td className="text-right whitespace-nowrap">{yuan(stats.totals.fundsTotal)}</td><td className="text-right">{stats.totals.goodsRecordCount} 条</td>
            </tr>
          </tbody>
        </table>
      </div>
      <Note>{stats.basis}{stats.lateRecordCount ? ` 截止后补报 ${stats.lateRecordCount} 条（不计入）。` : ""}{stats.outsideRecordCount ? ` 名单外填报 ${stats.outsideRecordCount} 条（不计入）。` : ""} 到场服务 {stats.totals.volunteerCount} 条。</Note>
    </DeskCard>
  );
}

function RecordsDesk({ task }: { task: TaskDetail }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const list = useRead<PageResult<RecordRow>>(`/api/v1/charity/records?periodYear=${task.periodYear}&periodQuarter=${task.periodQuarter}&limit=50`);
  return (
    <DeskCard title={`${period(task.periodYear, task.periodQuarter)}填报明细`} description="列表只给姓名、组织、活动与参与方式；金额与物资要逐条打开，每次打开都留存查看记录。不可按金额排序，不提供导出。">
      <ReadState {...list} retry={list.reload} />
      {list.data ? (
        list.data.items.length ? <div className="record-table-scroll">
          <table className="record-table">
            <caption className="sr-only">社会服务参与填报明细（不含金额）</caption>
            <thead><tr><th>会员</th><th>组织</th><th>活动</th><th>日期</th><th>参与方式</th><th /></tr></thead>
            <tbody>
              {list.data.items.map((row) => (
                <tr key={row.id}>
                  <td>{row.memberDisplayName}</td><td>{row.organization.name}</td>
                  <td>{row.title}{row.status === "WITHDRAWN" ? <span className="ml-2"><StatusTag tone="neutral">已撤回</StatusTag></span> : null}</td>
                  <td className="tabular-nums">{row.occurredOn}</td><td>{kinds(row.participationKinds)}</td>
                  <td className="text-right"><Button variant="outline" size="sm" onClick={() => setOpenId(openId === row.id ? null : row.id)}><Lock size={14} />{openId === row.id ? "收起" : "查看明细"}</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div> : <p className="py-6 text-xs text-muted-foreground">这一季度还没有填报记录。</p>
      ) : null}
      {openId ? <RecordDetailCard id={openId} /> : null}
    </DeskCard>
  );
}

function RecordDetailCard({ id }: { id: string }) {
  const detail = useRead<RecordDetail>(`/api/v1/charity/records/${id}`);
  return (
    <div className="mt-4 border-t pt-4">
      <ReadState {...detail} retry={detail.reload} />
      {detail.data ? <>
        <p className="mb-2 flex items-center gap-2 text-sm font-semibold"><HandHeart size={16} />{detail.data.title} · {detail.data.publicRef}</p>
        <Facts>
          <Fact label="会员">{detail.data.memberDisplayName} · {detail.data.organization.name}</Fact>
          <Fact label="活动日期">{detail.data.occurredOn}</Fact>
          <Fact label="参与方式">{kinds(detail.data.participationKinds)}</Fact>
          {detail.data.fundsAmount ? <Fact label="捐款金额">{yuan(detail.data.fundsAmount)}</Fact> : null}
          {detail.data.goodsDescription ? <Fact label="捐赠物资">{detail.data.goodsDescription}</Fact> : null}
          {detail.data.organizerLabel ? <Fact label="组织方">{detail.data.organizerLabel}</Fact> : null}
          {detail.data.note ? <Fact label="说明">{detail.data.note}</Fact> : null}
          <Fact label="经过">{detail.data.history.map((h) => `${ACTION[h.action] ?? h.action} ${formatTime(h.occurredAt)}`).join("；")}</Fact>
        </Facts>
        <Note>本人填报，未经核实。本次查看已留存记录。</Note>
      </> : null}
    </div>
  );
}
